import React, { useEffect, useState } from "react";
import Tabs from "components/lib/Shared/Tabs";
import ApprovalDocument from "./ApprovalDocument";
import { useSelector } from "react-redux";
import ESignature from "./ESignature";
import { Constants } from "config/constants";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/outline";
import MinuteBook from "./MinuteBook";

/**
 * Approvals component handles the display and logic for various approval document flows
 * (director, shareholder, court order, gazette, creditors) and e-signature integration.
 *
 * Props:
 * - officialIndex: Index of the official (if applicable)
 * - isViewMode: Boolean for view-only mode
 * - documents: Document data
 * - handleConfirmDetails: Callback for confirming e-signature details
 */
const Approvals = (props) => {
  const { transactions } = props
  // Redux selectors for transaction and approval data
  const { transactionUnderReview } = useSelector(
    (state) => state.transactionSlice
  );
  const { fileUploads, approvalDocs } =
    transactionUnderReview.items;
  const { officialIndex, isViewMode } = props;

  // Memoized tab definitions based on transaction types
  const initialTabs = React.useMemo(() => {
    if (!transactions.length) return [];
    let tabs = approvalDocs.map((doc) => {
      const key = Object.keys(doc)[0];
      return {
        id: key,
        label: key.split(/(?=[A-Z])/).join(" "), // Convert camelCase to spaced words
      };
    });

    const hasMinuteBookTransaction = transactions.some((transaction) => {
      const processTypesToCheck = [
        "privateWithMoreThanOneClass",
        "unallotedShares",
        "PVTwithOneClass",
        "existingAuth",
        "shareTransfer",
        "sharesConsolidation",
        "sharesSubdivision",
      ];
      return processTypesToCheck.includes(transaction.processType);
    });

    if (hasMinuteBookTransaction) {
      tabs.unshift({
        id: "minuteBook",
        label: "Minute Book",
      });
    }

    return tabs;
  }, [transactions]);

  // State for tabs and selection
  const [tabs, setTabs] = useState(initialTabs);
  const [selectedTab, setSelectedTab] = useState(tabs[0]);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [esignatureIsSelected, setEsignatureIsSelected] = useState(false);

  // Update selected tab when index changes
  useEffect(() => {
    setSelectedTab(tabs[selectedTabIndex]);
  }, [selectedTabIndex, tabs]);

  // Helper to get the correct document from approvalDocs by key
  const getApprovalDoc = (key) => {
    const docObj = approvalDocs.find((doc) => Object.keys(doc)[0] === key);
    return docObj ? docObj[key] : null;
  };

  const isLastStep = selectedTabIndex === tabs.length - 1;
  const isFirstStep = selectedTabIndex === 0;

  const goToPrevStep = () => {
    if (isFirstStep && props?.goToPrevParentTab) {
      props.goToPrevParentTab();
    } else {
      setSelectedTabIndex((prev) => prev - 1);
    }
  };

  const goToNextStep = () => {
    if (isLastStep && props?.goToNextParentTab) {
      props.goToNextParentTab();
    } else {
      setSelectedTabIndex((prev) => prev + 1);
    }
  };

  return (
    <div>
      {/* Tab navigation */}
      <Tabs
        tabs={tabs}
        selectedTabIndex={selectedTabIndex}
        setSelectedTabIndex={setSelectedTabIndex}
      />
      {/* E-signature flow */}
      {esignatureIsSelected ? (
        <div>
          <ESignature
            handleConfirmDetails={props.handleConfirmDetails}
            selectedTab={selectedTab}
            setEsignatureIsSelected={setEsignatureIsSelected}
          />
        </div>
      ) : (
        selectedTab?.id && (
          <div>
            {/* Render the appropriate ApprovalDocument based on selected tab */}
            {selectedTab.id === "minuteBook" && (
              <MinuteBook transactions={transactions} isViewMode={isViewMode} />
            )}
            {selectedTab.id === "directorApprovalDocs" && (
              <ApprovalDocument
                esignatureIsSelected={esignatureIsSelected}
                setEsignatureIsSelected={setEsignatureIsSelected}
                selectedTab="Director"
                fileUploads={fileUploads}
                document={getApprovalDoc("directorApprovalDocs")}
                documentType="directorApprovalDocs"
                signedDocumentType={"signedDirectorApprovalDocs"}
                isViewMode={isViewMode}
              />
            )}
            {selectedTab.id === "courtOrderApprovalDocs" && (
              <ApprovalDocument
                esignatureIsSelected={esignatureIsSelected}
                setEsignatureIsSelected={setEsignatureIsSelected}
                officialIndex={officialIndex}
                selectedTab="Court Order"
                document={getApprovalDoc("courtOrderApprovalDocs")}
                documentType="courtOrderApprovalDocs"
                signedDocumentType={"signedShareholderApprovalDocs"}
                isViewMode={isViewMode}
                fileUploads={fileUploads}
              />
            )}
            {selectedTab.id === "shareholderApprovalDocs" && (
              <ApprovalDocument
                esignatureIsSelected={esignatureIsSelected}
                setEsignatureIsSelected={setEsignatureIsSelected}
                officialIndex={officialIndex}
                selectedTab="Shareholder"
                document={getApprovalDoc("shareholderApprovalDocs")}
                documentType="shareholderApprovalDocs"
                signedDocumentType={"shareholderApprovalSignedDocuments"}
                isViewMode={isViewMode}
                fileUploads={fileUploads}
              />
            )}
            {selectedTab.id === "gazetteApprovalDocs" && (
              <ApprovalDocument
                esignatureIsSelected={esignatureIsSelected}
                setEsignatureIsSelected={setEsignatureIsSelected}
                officialIndex={officialIndex}
                selectedTab="Gazette"
                document={getApprovalDoc("gazetteApprovalDocs")}
                documentType="gazetteApprovalDocs"
                signedDocumentType={"gazetteApprovalSignedDocuments"}
                isViewMode={isViewMode}
                fileUploads={fileUploads}
              />
            )}
            {selectedTab.id === "creditorApprovalDocs" && (
              <ApprovalDocument
                esignatureIsSelected={esignatureIsSelected}
                setEsignatureIsSelected={setEsignatureIsSelected}
                officialIndex={officialIndex}
                selectedTab="Creditor"
                document={getApprovalDoc("creditorApprovalDocs")}
                documentType="creditorApprovalDocs"
                signedDocumentType={"creditorApprovalSignedDocuments"}
                isViewMode={isViewMode}
                fileUploads={fileUploads}
              />
            )}
          </div>
        )
      )}
      <div className="flex justify-between">
        {
          <button
            onClick={goToPrevStep}
            className="flex items-start float-right py-2 font-semibold rounded text-primary mt-7"
          >
            <span className="flex items-center mr-4 h-7">
              <ChevronLeftIcon
                className="flex-shrink-0 w-5 h-5"
                aria-hidden="true"
              />
            </span>
            <p className="font-semibold">Go back</p>
          </button>
        }
        <button
          onClick={goToNextStep}
          className="flex items-start float-right py-2 ml-auto font-semibold rounded text-primary mt-7"
        >
          <p className="font-semibold">Next step</p>
          <span className="flex items-center ml-4 h-7">
            <ChevronRightIcon
              className="flex-shrink-0 w-5 h-5"
              aria-hidden="true"
            />
          </span>
        </button>
      </div>
    </div>
  );
};

export default Approvals;
